import { formatTime } from "./dateUtils.js";

/** Statuses fetched from the backend for the board. DELIVERED is never requested. */
export const BOARD_ACTIVE_STATUSES = ["REQUESTED", "PREPARING", "READY_FOR_DELIVERY", "DELIVERY_IN_PROGRESS"];

/** Grace period after a local move to DELIVERED before the backend is called. */
export const DELIVERED_UNDO_MS = 5000;

/** Board columns, left to right. DELIVERED only holds cards moved there during this session. */
export const BOARD_COLUMNS = [...BOARD_ACTIVE_STATUSES, "DELIVERED"];

export const NO_COMPANY_KEY = "__no_company__";

export const BOARD_SCOPE = {
    TODAY: "TODAY",
    ALL: "ALL"
};

const pad2 = (n) => String(n).padStart(2, "0");

export const formatLocalDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** Local whole-day bounds in the format the orders API expects. */
export const todayDeliveryBounds = (now = new Date()) => {
    const day = formatLocalDate(now);
    return { deliveryDateFrom: `${day}T00:00:00`, deliveryDateTo: `${day}T23:59:59` };
};

export const isValidBoardOrder = (order) =>
    order != null && order.id != null && order.status != null;

export const companyKeyOf = (order) => {
    const name = order?.companyName?.trim();
    return name ? name : NO_COMPANY_KEY;
};

/** Clock time shown on a board card (HH:mm, no timezone shift). */
export const boardOrderTime = (order) => formatTime(order?.deliveryDateTime);

/** Unique delivery times currently on the board, earliest first. */
export const uniqueBoardTimes = (orders) => {
    const times = new Set();
    for (const order of orders || []) {
        const time = boardOrderTime(order);
        if (time) times.add(time);
    }
    return [...times].sort();
};

/** Client-only: keep orders whose displayed time matches. Empty/null time shows all. */
export const filterBoardOrdersByTime = (orders, time) => {
    if (!time) return orders || [];
    return (orders || []).filter((order) => boardOrderTime(order) === time);
};

const compareByDelivery = (a, b) => {
    const at = a.deliveryDateTime != null ? String(a.deliveryDateTime) : "\uffff";
    const bt = b.deliveryDateTime != null ? String(b.deliveryDateTime) : "\uffff";
    if (at !== bt) return at < bt ? -1 : 1;
    return String(a.number ?? a.id).localeCompare(String(b.number ?? b.id), undefined, { numeric: true });
};

/**
 * Apply a poll result to the board without disturbing it.
 * - Orders not seen before are added and flagged as new.
 * - Orders moved to DELIVERED in this session stay until the page is reloaded.
 * - Orders with a status update in flight, or a delayed DELIVERED commit, keep their local status.
 * - Other orders missing from the result (delivered/declined elsewhere) are removed.
 *
 * @param {{orders: Object<string, Object>, newIds: Set}} prev
 * @param {Object[]} fetched
 * @param {{pendingIds?: Set, deliveredIds?: Set, markNew?: boolean}} options
 * @returns {{orders: Object<string, Object>, newIds: Set}}
 */
export const mergeBoardOrders = (prev, fetched, { pendingIds = new Set(), deliveredIds = new Set(), markNew = true } = {}) => {
    const orders = {};
    const newIds = new Set();
    const prevOrders = prev?.orders || {};
    const prevNew = prev?.newIds || new Set();

    for (const order of fetched || []) {
        if (!isValidBoardOrder(order)) continue;
        const id = String(order.id);
        const known = Object.prototype.hasOwnProperty.call(prevOrders, id);
        orders[id] = pendingIds.has(id) && known ? prevOrders[id] : order;
        if (known ? prevNew.has(id) : markNew) newIds.add(id);
    }

    for (const [id, order] of Object.entries(prevOrders)) {
        if (orders[id]) continue;
        if (pendingIds.has(id) || (deliveredIds.has(id) && order.status === "DELIVERED")) {
            orders[id] = order;
            if (prevNew.has(id)) newIds.add(id);
        }
    }

    return { orders, newIds };
};

/**
 * Group orders into company swimlanes with one bucket per column.
 * Lanes are ordered by their earliest upcoming delivery; cards inside a cell by delivery time.
 *
 * @param {Object[]} orders
 * @returns {{key: string, companyName: string|null, count: number, cells: Object<string, Object[]>}[]}
 */
export const groupBoardLanes = (orders) => {
    const lanes = new Map();

    for (const order of orders) {
        if (!BOARD_COLUMNS.includes(order.status)) continue;
        const key = companyKeyOf(order);
        if (!lanes.has(key)) {
            lanes.set(key, {
                key,
                companyName: key === NO_COMPANY_KEY ? null : key,
                count: 0,
                activeCount: 0,
                firstActive: null,
                firstAny: null,
                cells: Object.fromEntries(BOARD_COLUMNS.map((s) => [s, []]))
            });
        }
        const lane = lanes.get(key);
        lane.cells[order.status].push(order);
        lane.count += 1;
        const dt = String(order.deliveryDateTime);
        if (order.status !== "DELIVERED") {
            lane.activeCount += 1;
            if (lane.firstActive == null || dt < lane.firstActive) lane.firstActive = dt;
        }
        if (lane.firstAny == null || dt < lane.firstAny) lane.firstAny = dt;
    }

    const result = [...lanes.values()];
    for (const lane of result) {
        for (const status of BOARD_COLUMNS) lane.cells[status].sort(compareByDelivery);
    }

    result.sort((a, b) => {
        // Lanes with nothing left to do sink to the bottom.
        if ((a.firstActive == null) !== (b.firstActive == null)) return a.firstActive == null ? 1 : -1;
        const at = a.firstActive ?? a.firstAny;
        const bt = b.firstActive ?? b.firstAny;
        if (at !== bt) return at < bt ? -1 : 1;
        if (a.key === NO_COMPANY_KEY) return 1;
        if (b.key === NO_COMPANY_KEY) return -1;
        return a.key.localeCompare(b.key);
    });

    return result;
};
